(() => {
  const SORT_OPTIONS = [
    { value: 'manual', label: 'Recommended' },
    { value: 'price-ascending', label: 'Price: Low to High' },
    { value: 'price-descending', label: 'Price: High to Low' }
  ];

  const SIZE_DEFINITIONS = [
    { label: 'EXTRA SMALL', aliases: ['XS', 'X SMALL', 'EXTRA SMALL'] },
    { label: 'SMALL', aliases: ['S', 'SMALL'] },
    { label: 'MEDIUM', aliases: ['M', 'MEDIUM'] },
    { label: 'LARGE', aliases: ['L', 'LARGE'] },
    { label: 'X-LARGE', aliases: ['XL', 'X LARGE', 'EXTRA LARGE'] },
    { label: 'XX LARGE', aliases: ['XXL', '2XL', 'XX LARGE', 'DOUBLE EXTRA LARGE'] }
  ];

  const cleanLabel = (value) =>
    value
      .replace(/\s*\(\s*\d+\s*\)\s*$/, '')
      .replace(/\s+/g, ' ')
      .trim();

  const normalizeSizeToken = (value) =>
    cleanLabel(value)
      .toUpperCase()
      .replace(/[._/-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const canonicalSizeLabel = (value) => {
    const token = normalizeSizeToken(value);
    const definition = SIZE_DEFINITIONS.find((item) => item.aliases.includes(token));
    return definition?.label || null;
  };

  const findAccordion = (name) => {
    const accordions = document.querySelectorAll('.facets-container .facets__form > .facet-accordion');
    return Array.from(accordions).find((accordion) => {
      const title = accordion.querySelector('.facet-accordion__title');
      return title?.textContent.trim().toUpperCase() === name;
    });
  };

  const getSizeOptions = (accordion) => {
    const rawOptions = [];
    const seen = new Set();
    const url = new URL(window.location.href);

    accordion?.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      const labelElement = input.closest('label') || accordion.querySelector(`label[for="${input.id}"]`);
      const sourceLabel = cleanLabel(labelElement?.textContent || input.value);
      const label = canonicalSizeLabel(sourceLabel);
      const key = `${input.name}::${input.value}`;

      if (!label || !input.name || !input.value || seen.has(key)) return;
      seen.add(key);

      rawOptions.push({
        name: input.name,
        value: input.value,
        label,
        checked: input.checked || url.searchParams.getAll(input.name).includes(input.value),
        disabled: input.disabled || input.classList.contains('disabled')
      });
    });

    return SIZE_DEFINITIONS.map((definition) => {
      const entries = rawOptions.filter((option) => option.label === definition.label);
      return {
        label: definition.label,
        entries,
        checked: entries.some((entry) => entry.checked),
        disabled: entries.length === 0 || entries.every((entry) => entry.disabled)
      };
    });
  };

  const getAvailabilityState = (accordion) => {
    const sourceInput = accordion?.querySelector('input[type="checkbox"]');
    const url = new URL(window.location.href);
    return sourceInput?.checked || url.searchParams.getAll('filter.v.availability').includes('1');
  };

  const createHeading = (text) => {
    const heading = document.createElement('div');
    heading.className = 'jeanius-filter-menu__heading';
    heading.textContent = text;
    return heading;
  };

  const createChoice = ({
    type,
    name,
    value,
    label,
    checked = false,
    disabled = false,
    radio = false,
    filterPairs = null
  }) => {
    const wrapper = document.createElement('label');
    wrapper.className = `jeanius-filter-menu__choice jeanius-filter-menu__choice--${type}`;
    if (disabled) wrapper.classList.add('is-disabled');

    const input = document.createElement('input');
    input.type = radio ? 'radio' : 'checkbox';
    input.name = name;
    input.value = value;
    input.checked = checked;
    input.disabled = disabled;
    input.dataset.jeaniusFilterType = type;

    if (filterPairs) {
      input.dataset.jeaniusFilterPairs = JSON.stringify(filterPairs);
    }

    const text = document.createElement('span');
    text.className = 'jeanius-filter-menu__choice-text';
    text.textContent = label;

    wrapper.append(input, text);
    return wrapper;
  };

  const getFilterPairs = (input) => {
    try {
      return JSON.parse(input.dataset.jeaniusFilterPairs || '[]');
    } catch (error) {
      return [];
    }
  };

  const buildControls = ({ sizeOptions, availabilityChecked, scopeName }) => {
    const controls = document.createElement('div');
    controls.className = 'jeanius-filter-menu__controls';
    controls.dataset.jeaniusFilterScope = scopeName;

    const currentUrl = new URL(window.location.href);
    const currentSort = currentUrl.searchParams.get('sort_by') || 'manual';

    const sortSection = document.createElement('section');
    sortSection.className = 'jeanius-filter-menu__section';
    sortSection.append(createHeading('SORT BY'));

    const sortChoices = document.createElement('div');
    sortChoices.className = 'jeanius-filter-menu__choices jeanius-filter-menu__choices--stacked';
    SORT_OPTIONS.forEach((option) => {
      sortChoices.append(
        createChoice({
          type: 'sort',
          name: `jeanius-sort-${scopeName}`,
          value: option.value,
          label: option.label,
          checked: currentSort === option.value,
          radio: true
        })
      );
    });
    sortSection.append(sortChoices);
    controls.append(sortSection);

    const sizeSection = document.createElement('section');
    sizeSection.className = 'jeanius-filter-menu__section';
    sizeSection.append(createHeading('SIZE'));

    const sizeChoices = document.createElement('div');
    sizeChoices.className =
      'jeanius-filter-menu__choices jeanius-filter-menu__choices--stacked jeanius-filter-menu__choices--sizes';

    sizeOptions.forEach((option, index) => {
      sizeChoices.append(
        createChoice({
          type: 'size',
          name: `jeanius-size-${scopeName}`,
          value: String(index),
          label: option.label,
          checked: option.checked,
          disabled: option.disabled,
          filterPairs: option.entries.map(({ name, value }) => ({ name, value }))
        })
      );
    });
    sizeSection.append(sizeChoices);
    controls.append(sizeSection);

    const availabilitySection = document.createElement('section');
    availabilitySection.className = 'jeanius-filter-menu__section';
    availabilitySection.append(createHeading('AVAILABILITY'));

    const availabilityChoices = document.createElement('div');
    availabilityChoices.className = 'jeanius-filter-menu__choices jeanius-filter-menu__choices--stacked';
    availabilityChoices.append(
      createChoice({
        type: 'availability',
        name: 'filter.v.availability',
        value: '1',
        label: 'In Stock',
        checked: availabilityChecked
      })
    );
    availabilitySection.append(availabilityChoices);
    controls.append(availabilitySection);

    controls.addEventListener('change', (event) => {
      const input = event.target.closest('input[data-jeanius-filter-type]');
      if (!input) return;

      const url = new URL(window.location.href);
      url.searchParams.delete('page');

      switch (input.dataset.jeaniusFilterType) {
        case 'sort':
          url.searchParams.set('sort_by', input.value);
          break;
        case 'size': {
          const sizeInputs = Array.from(
            controls.querySelectorAll('input[data-jeanius-filter-type="size"]')
          );
          const parameterNames = new Set();

          sizeInputs.forEach((sizeInput) => {
            getFilterPairs(sizeInput).forEach(({ name }) => {
              if (name) parameterNames.add(name);
            });
          });

          parameterNames.forEach((name) => url.searchParams.delete(name));

          sizeInputs
            .filter((sizeInput) => sizeInput.checked)
            .forEach((sizeInput) => {
              getFilterPairs(sizeInput).forEach(({ name, value }) => {
                if (name && value) url.searchParams.append(name, value);
              });
            });
          break;
        }
        case 'availability':
          url.searchParams.delete('filter.v.availability');
          if (input.checked) url.searchParams.set('filter.v.availability', '1');
          break;
        default:
          return;
      }

      window.location.assign(url.toString());
    });

    return controls;
  };

  const buildDesktopMenu = (controls) => {
    const menu = document.createElement('div');
    menu.className = 'jeanius-filter-menu';

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'jeanius-filter-menu__trigger';
    trigger.setAttribute('aria-expanded', 'false');
    trigger.textContent = 'FILTER';

    const panel = document.createElement('div');
    panel.className = 'jeanius-filter-menu__panel';
    panel.hidden = true;
    panel.append(controls);

    const close = () => {
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      menu.classList.remove('is-open');
    };

    trigger.addEventListener('click', (event) => {
      event.stopPropagation();
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        close();
      } else {
        panel.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        menu.classList.add('is-open');
      }
    });

    menu.addEventListener('click', (event) => event.stopPropagation());
    document.addEventListener('click', close);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });

    menu.append(trigger, panel);
    return menu;
  };

  const start = () => {
    if (!document.body.classList.contains('template-collection')) return;

    const desktopSort = document.querySelector('.collection-top__sort .facet-sort-select');
    const sidebarForm = document.querySelector('.facets-container .facets__form');
    if (!desktopSort || !sidebarForm) return;

    const sizeAccordion = findAccordion('SIZE');
    const availabilityAccordion = findAccordion('AVAILABILITY');
    const sizeOptions = getSizeOptions(sizeAccordion);
    const availabilityChecked = getAvailabilityState(availabilityAccordion);

    const desktopControls = buildControls({
      sizeOptions,
      availabilityChecked,
      scopeName: 'desktop'
    });
    desktopSort.replaceWith(buildDesktopMenu(desktopControls));

    const accordions = Array.from(sidebarForm.querySelectorAll(':scope > .facet-accordion'));
    accordions.slice(1).forEach((accordion) => accordion.remove());
    sidebarForm.classList.add('jeanius-collections-only');

    const mobileSortButton = document.querySelector('.collection-top__mobile-bar .js-mobile-sort-btn');
    mobileSortButton?.remove();

    const mobileSortDrawer = document.querySelector('.js-mobile-sort-drawer');
    mobileSortDrawer?.remove();

    const mobileFilterDrawer = document.querySelector('.js-mobile-filter-drawer');
    const mobileDrawerContent = mobileFilterDrawer?.querySelector('.mobile-drawer__content');
    const mobileDrawerTitle = mobileFilterDrawer?.querySelector('.mobile-drawer__title');
    if (mobileDrawerContent) {
      mobileDrawerContent.replaceChildren(
        buildControls({
          sizeOptions,
          availabilityChecked,
          scopeName: 'mobile'
        })
      );
      if (mobileDrawerTitle) mobileDrawerTitle.textContent = 'FILTER';
      mobileFilterDrawer.classList.add('jeanius-filter-drawer');
    }

    const mobileFilterButton = document.querySelector('.collection-top__mobile-bar .js-mobile-filter-btn');
    if (mobileFilterButton) {
      const textNode = Array.from(mobileFilterButton.childNodes).find(
        (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim()
      );
      if (textNode) textNode.textContent = 'FILTER ';
    }

    if (mobileFilterButton && mobileFilterDrawer) {
      if (!mobileFilterDrawer.id) mobileFilterDrawer.id = 'JeaniusMobileFilterDrawer';
      mobileFilterButton.setAttribute('aria-controls', mobileFilterDrawer.id);

      const syncMobileFilterState = () => {
        const isOpen = mobileFilterDrawer.classList.contains('active');
        mobileFilterButton.classList.toggle('is-active', isOpen);
        mobileFilterButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      };

      const mobileDrawerObserver = new MutationObserver(syncMobileFilterState);
      mobileDrawerObserver.observe(mobileFilterDrawer, {
        attributes: true,
        attributeFilter: ['class']
      });
      syncMobileFilterState();
    }

    document.documentElement.classList.add('jeanius-filter-menu-ready');
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
